import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Crown, Building2, Send, ChevronRight, Sparkles, Globe2, Lock,
  TrendingUp, BookOpen, MessageSquareQuote, CalendarClock, Tag,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { RiskBanner } from "@/components/shared/RiskBanner";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Icon } from "@/lib/icons";
import { localizeDeep } from "@/lib/localize";

const fadeIn = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

export default function Home() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  useSEO("", "INVIXA TRADERS — a complete trading ecosystem for education, VIP membership, broker partnerships and performance transparency.");
  const [articles, setArticles] = useState([]);
  const [results, setResults] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [cms, setCms] = useState({});

  useEffect(() => {
    api.get("/education/articles").then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
    api.get("/results").then((r) => setResults(r.data.slice(0, 3))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data.slice(0, 3))).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data.slice(0, 5))).catch(() => {});
    api.get("/content/home").then((r) => setCms(r.data.value || {})).catch(() => {});
  }, []);

  const defaultServices = [
    { title: t("svc_edu_title"), description: t("svc_edu_desc"), icon: "GraduationCap" },
    { title: t("svc_vip_title"), description: t("svc_vip_desc"), icon: "Crown" },
    { title: t("svc_brokers_title"), description: t("svc_brokers_desc"), icon: "Building2" },
    { title: t("svc_perf_title"), description: t("svc_perf_desc"), icon: "LineChart" },
  ];
  const vipItems = [
    { icon: CalendarClock, title: t("vip_item_monthly"), desc: t("vip_item_monthly_desc") },
    { icon: Building2, title: t("vip_item_broker"), desc: t("vip_item_broker_desc") },
    { icon: Tag, title: t("vip_item_promo"), desc: t("vip_item_promo_desc") },
    { icon: Send, title: t("vip_item_telegram"), desc: t("vip_item_telegram_desc") },
  ];

  const lc = localizeDeep(cms, lang);
  const hero = lc.hero || {};
  const why = lc.why || {};
  const sec = lc.sections || {};
  const finalCta = lc.final_cta || {};
  const services = (lc.services && lc.services.length ? lc.services.filter((s) => s.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0)) : defaultServices);
  const secOn = (k) => sec[k]?.enabled !== false;

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden radial-cyan">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-20 lg:pt-28 lg:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="show" variants={fadeIn}>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-cyan/30 bg-brand-cyan/10 px-4 py-1.5 text-xs font-mono uppercase tracking-widest text-brand-cyan">
                <Sparkles className="h-3.5 w-3.5" /> {hero.eyebrow || "Premium Trading Ecosystem"}
              </span>
              <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
                {hero.title || t("hero_title")}
                <span className="block mt-2 text-gradient-brand">{hero.highlight || t("hero_subtitle")}</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl">{hero.subtitle || t("hero_desc")}</p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <Button onClick={() => navigate(hero.cta1_link || "/vip")} data-testid="hero-vip-cta"
                  className="gradient-brand text-brand-navy font-semibold text-base px-7 py-6 rounded-full hover:opacity-90 glow-cyan">
                  <Crown className="h-5 w-5 mr-2" /> {hero.cta1_text || t("hero_cta_vip")}
                </Button>
                <Button onClick={() => navigate(hero.cta2_link || "/brokers")} data-testid="hero-broker-cta" variant="outline"
                  className="border-white/15 text-white text-base px-7 py-6 rounded-full hover:bg-white/5 hover:border-brand-cyan/50">
                  {hero.cta2_text || t("hero_cta_broker")} <ArrowRight className="h-5 w-5 ml-2 rtl-mirror" />
                </Button>
              </div>
              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-500">
                <span className="flex items-center gap-2"><Globe2 className="h-4 w-4 text-brand-turquoise" /> EN / العربية</span>
                <span className="flex items-center gap-2"><Lock className="h-4 w-4 text-brand-turquoise" /> {t("badge_secure")}</span>
                <span className="flex items-center gap-2"><Send className="h-4 w-4 text-brand-turquoise" /> {t("badge_vip_tg")}</span>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="relative hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden border border-white/10 glow-cyan animate-floaty">
                <img src={hero.image_file_id ? fileUrl(hero.image_file_id) : "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"} alt={hero.image_alt || "Trading analytics"} className="w-full h-[520px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/40 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 card-surface glass rounded-2xl p-5">
                  <p className="font-mono text-xs text-brand-cyan uppercase tracking-widest">Ecosystem</p>
                  <p className="mt-1 text-white font-display font-semibold">Education · VIP · Brokers · Community</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY INVIXA — 4 core ecosystem cards */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <SectionHeading center eyebrow={t("why_invixa")} title={why.title || t("why_title")}
          subtitle={why.description || t("why_subtitle")} />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
              <motion.div key={s.id || i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeIn} transition={{ delay: i * 0.05 }}
                className="card-surface hover-glow rounded-2xl p-6" data-testid={`service-card-${i}`}>
                <div className="h-11 w-11 rounded-xl gradient-brand flex items-center justify-center mb-4">
                  <Icon name={s.icon} className="h-5 w-5 text-brand-navy" />
                </div>
                <h3 className="font-display text-base font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.description}</p>
              </motion.div>
          ))}
        </div>
      </section>

      {/* VIP band — compact benefits list */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="card-surface rounded-3xl overflow-hidden border-brand-cyan/20 relative">
            <div className="absolute inset-0 gradient-brand opacity-[0.07]" />
            <div className="relative grid lg:grid-cols-2 gap-10 p-8 lg:p-12 items-center">
              <div>
                <span className="font-mono text-xs uppercase tracking-widest text-brand-cyan">{t("vip_section")}</span>
                <h2 className="font-display mt-3 text-2xl sm:text-3xl font-bold text-white">{t("vip_band_title")}</h2>
                <p className="mt-4 text-slate-400 leading-relaxed">{t("vip_band_desc")}</p>
                <div className="mt-6 flex flex-wrap gap-4">
                  <Button onClick={() => navigate("/vip")} className="gradient-brand text-brand-navy font-semibold rounded-full px-6" data-testid="vip-band-cta">{t("hero_cta_vip")}</Button>
                  <Button onClick={() => navigate("/brokers")} variant="outline" className="border-white/15 text-white rounded-full px-6">{t("partner_brokers")}</Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {vipItems.map((it, i) => (
                  <div key={i} className="flex items-start gap-3" data-testid={`vip-band-item-${i}`}>
                    <div className="h-9 w-9 rounded-lg bg-brand-cyan/10 flex items-center justify-center shrink-0">
                      <it.icon className="h-4.5 w-4.5 text-brand-cyan" />
                    </div>
                    <div>
                      <p className="font-display font-semibold text-white text-sm">{it.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{it.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Education preview */}
      {secOn("education") && (
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <SectionHeading eyebrow={t("education_preview")} title={sec.education?.title || t("edu_home_title")} />
          {articles.length > 0 && <Link to="/education" className="text-sm text-brand-cyan hover:underline flex items-center gap-1" data-testid="home-edu-viewall">{t("view_all")} <ChevronRight className="h-4 w-4 rtl-mirror" /></Link>}
        </div>
        {articles.length === 0 ? (
          <div className="mt-8 card-surface rounded-2xl py-12 flex flex-col items-center justify-center text-center" data-testid="home-edu-empty">
            <BookOpen className="h-8 w-8 text-slate-600 mb-3" />
            <p className="text-sm text-slate-500">{t("edu_empty")}</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {articles.map((a) => (
              <Link key={a.id} to={`/education/${a.slug}`} className="card-surface hover-glow rounded-2xl overflow-hidden group" data-testid={`home-article-${a.id}`}>
                <div className="h-40 bg-brand-elevated overflow-hidden">
                  {a.cover_image_file_id ? <img src={fileUrl(a.cover_image_file_id)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <div className="w-full h-full flex items-center justify-center"><BookOpen className="h-8 w-8 text-slate-600" /></div>}
                </div>
                <div className="p-5">
                  <p className="font-mono text-[11px] text-brand-cyan uppercase">{a.reading_time || 5} {t("min_read")}</p>
                  <h3 className="mt-2 font-display font-semibold text-white line-clamp-2">{a.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 line-clamp-2">{a.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
      )}

      {/* Results preview */}
      {secOn("results") && (
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <SectionHeading eyebrow={t("results_preview")} title={sec.results?.title || t("results_home_title")} subtitle={t("past_performance")} />
          {results.length > 0 && <Link to="/results" className="text-sm text-brand-cyan hover:underline flex items-center gap-1" data-testid="home-results-viewall">{t("view_all")} <ChevronRight className="h-4 w-4 rtl-mirror" /></Link>}
        </div>
        {results.length === 0 ? (
          <div className="mt-8 card-surface rounded-2xl py-12 flex flex-col items-center justify-center text-center" data-testid="home-results-empty">
            <TrendingUp className="h-8 w-8 text-slate-600 mb-3" />
            <p className="text-sm text-slate-500">{t("results_empty")}</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {results.map((r) => (
              <div key={r.id} className="card-surface hover-glow rounded-2xl overflow-hidden" data-testid={`home-result-${r.id}`}>
                {r.image_file_id && <img src={fileUrl(r.image_file_id)} alt={r.title} className="w-full h-40 object-cover" />}
                <div className="p-5">
                  <span className="font-mono text-[11px] uppercase text-brand-turquoise">{r.type} · {r.date}</span>
                  <h3 className="mt-2 font-display font-semibold text-white">{r.title}</h3>
                  <p className="mt-1 text-sm text-slate-400 line-clamp-2">{r.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      )}

      {/* Testimonials */}
      {secOn("testimonials") && testimonials.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <SectionHeading center eyebrow={t("testimonials")} title={sec.testimonials?.title || t("testimonials_title")} />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((tm) => (
              <div key={tm.id} className="card-surface rounded-2xl overflow-hidden" data-testid={`home-testimonial-${tm.id}`}>
                {tm.image_file_id && <img src={fileUrl(tm.image_file_id)} alt={tm.name || "Testimonial"} className="w-full object-cover" />}
                <div className="p-5">
                  <MessageSquareQuote className="h-5 w-5 text-brand-cyan mb-2" />
                  {tm.description && <p className="text-sm text-slate-300">{tm.description}</p>}
                  {tm.name && <p className="mt-3 text-xs text-slate-500">— {tm.name}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Community CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="rounded-3xl gradient-brand p-[1px]">
          <div className="rounded-3xl bg-brand-navy2 px-8 py-12 lg:py-14 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">{finalCta.heading || t("community")}</h2>
            <p className="mt-4 text-slate-400 max-w-xl mx-auto">{finalCta.description || t("community_desc")}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate(finalCta.cta_link || "/signup")} className="gradient-brand text-brand-navy font-semibold rounded-full px-8 py-6 text-base" data-testid="community-signup">{finalCta.cta_text || t("get_started")}</Button>
              <Button onClick={() => navigate("/contact")} variant="outline" className="border-white/15 text-white rounded-full px-8 py-6 text-base" data-testid="community-contact">{t("nav_contact")}</Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ preview */}
      {secOn("faq") && faqs.length > 0 && (
        <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12">
          <SectionHeading center eyebrow="FAQ" title={sec.faq?.title || t("faq")} />
          <Accordion type="single" collapsible className="mt-8" data-testid="home-faq">
            {faqs.map((f) => (
              <AccordionItem key={f.id} value={f.id} className="card-surface rounded-xl mb-3 px-5 border-white/10">
                <AccordionTrigger className="text-white hover:no-underline text-left">{f.question}</AccordionTrigger>
                <AccordionContent className="text-slate-400">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      <RiskBanner />
    </div>
  );
}
